import { ReactNode } from 'react'
import { usePermissions } from '../hooks/usePermissions'
import UnauthorizedPage from '../pages/UnauthorizedPage'
import { logger } from '../utils/logger'
import { canAccessScreen, type ScreenId } from '../access/screens'
import type { PermissionKey } from '../types/permissions'

interface ProtectedRouteProps {
  children: ReactNode
  requiredPermission?: keyof ReturnType<typeof usePermissions>['can']
  requiredRole?: 'ADMIN' | 'AGENT' | 'USER'
  /**
   * L-NAV-9 — l'écran du catalogue `access/screens.ts` : la route lit la MÊME
   * condition que le menu (une permission par écran, aux trois étages).
   */
  screen?: ScreenId
  /**
   * L-NAV-9 — un drapeau brut (onglet d'une page : Historique, Modèles), lu via
   * `hasPermission` (compat descendante + passe-droit du compte système).
   */
  requiredPermissionKey?: PermissionKey
}

/**
 * Composant pour protéger les routes selon le rôle ou la permission
 * Optimized: No console logs in production, instant permission checks
 */
export default function ProtectedRoute({
  children,
  requiredPermission,
  requiredRole,
  screen,
  requiredPermissionKey,
}: ProtectedRouteProps) {
  const { can, user, isLoading, isSystemAdmin, hasPermission } = usePermissions()

  // Wait for loading to complete before checking permissions
  // Return null (not a loader) to avoid visual flicker
  if (isLoading) {
    return null
  }

  // L-NAV-9 : le compte système passe partout, sans dépendre d'un drapeau
  // (même règle que `PermissionsGuard` côté serveur). Les gardes par rôle
  // ci-dessous le laissent passer aussi : il est ADMIN.
  if (screen && !canAccessScreen(screen, { role: user?.role, isSystemAdmin, hasPermission })) {
    logger.warn(`🚫 Access denied: screen ${screen}`, { user: user?.email, role: user?.role })
    return <UnauthorizedPage />
  }

  if (requiredPermissionKey && !hasPermission(requiredPermissionKey)) {
    logger.warn(`🚫 Access denied: Missing permission ${requiredPermissionKey}`, { user: user?.email, role: user?.role })
    return <UnauthorizedPage />
  }

  // Vérifier le rôle spécifique si demandé
  if (requiredRole && user?.role !== requiredRole) {
    logger.warn(`🚫 Access denied: Required role ${requiredRole}, current role ${user?.role}`)
    return <UnauthorizedPage />
  }

  // Vérifier la permission si demandée
  if (requiredPermission && !can[requiredPermission]) {
    logger.warn(`🚫 Access denied: Missing permission ${requiredPermission}`, { 
      user: user?.email,
      role: user?.role 
    })
    return <UnauthorizedPage />
  }

  // Autoriser l'accès
  return children
}
